import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  Renderer2,
  RendererStyleFlags2,
  ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
  FuseHorizontalNavigationComponent,
  FuseNavigationService,
  FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { fromEvent, merge } from 'rxjs';
import { LogoComponent } from '../../../../../shared/components/logo.component';
import { debounceTime, delay, filter } from 'rxjs/operators';
import { IconMenuItemsComponent } from '../../../../../shared/components/icon-menu-items.component';
import { UntilDestroy } from '@ngneat/until-destroy';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LayoutService } from 'app/shared/services/layout.service';
import _ from 'lodash';
import { NavigationService } from '../../../../navigation/navigation.service';
import { ImpersonationBannerComponent } from 'app/core/layout/common/impersonation-banner/impersonation-banner.component';
import { DrawerInsetDirective } from 'app/shared/directives/drawer-inset.directive';
import { PoweredByComponent } from 'app/core/layout/common/powered-by/powered-by.component';
import { SystemSettingsStore } from 'app/core/services/system-settings.store';
import { getPoweredByLogoHeight } from 'app/core/services/theme.model';
import { publishStickyHeaderOffset } from 'app/core/layout/sticky-header-offset';

@UntilDestroy()
@Component({
  selector: 'modern-layout',
  templateUrl: './modern.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FuseLoadingBarComponent,
    FuseVerticalNavigationComponent,
    FuseHorizontalNavigationComponent,
    MatButtonModule,
    MatIconModule,
    RouterOutlet,
    LogoComponent,
    IconMenuItemsComponent,
    ImpersonationBannerComponent,
    DrawerInsetDirective,
    PoweredByComponent,
  ],
  styles: `
    .fuse-horizontal-navigation-menu-item:has(.menu-item--return) {
      margin-left: auto !important;
    }
  `,
})
export class ModernLayoutComponent implements AfterViewInit {
  navigationService = inject(NavigationService);
  layoutService = inject(LayoutService);
  systemSettingsStore = inject(SystemSettingsStore);
  /** Footer supplier-logo height, admin-configurable per tenant (#21262 follow-up). Clamped/defaulted
   *  by `getPoweredByLogoHeight` — `dd-powered-by` itself stays presentational (input only). */
  poweredByLogoHeight = computed(() => getPoweredByLogoHeight(this.systemSettingsStore.branding()));
  private resizeObserver: ResizeObserver;

  /**
   * Constructor
   */
  constructor(
    private _router: Router,
    private _fuseNavigationService: FuseNavigationService,
    private _elementRef: ElementRef,
    private _renderer: Renderer2
  ) {
    // The bar is pinned (`sticky top-0` in the template): tell the consumers how tall it is.
    publishStickyHeaderOffset();

    // Throttle with a LEADING edge, not a trailing-only debounce (changed 2026-08-25).
    //
    // `shrinkNavigationOnOverflow` is a JS layout pass — it measures scrollWidth and writes inline
    // font-size/padding/icon-size/button-width — so it can never follow a CSS transition frame by
    // frame. With a plain `_.debounce(…, 100)` it fired ONLY 100ms after the last resize, which put
    // it a full 500ms behind the start of the widget-drawer slide: the panel came in, the content
    // glided across, everything settled, and only THEN did every nav item and icon button snap to a
    // new size in one visible jump (user report; measured — nav width held at 811px through the
    // 400ms transition, then stepped to 846px at t≈600ms).
    //
    // Leading edge makes the first measurement happen on the very first resize notification, which
    // — together with the trailing cluster's margin no longer being transitioned, see
    // `.dd-drawer-inset-trailing` in _widgets.scss — lets the nav reach its final shape in the same
    // frame the gesture starts, while the drawer is still sliding. The trailing edge is kept so a
    // continuous window-drag still gets a final, correct pass once it stops.
    this.resizeObserver = new ResizeObserver(
      _.throttle(
        () => {
          this.shrinkNavigationOnOverflow();
        },
        100,
        { leading: true, trailing: true }
      )
    );

    merge(
      fromEvent(window, 'resize').pipe(debounceTime(100)),
      this._router.events.pipe(filter(event => event instanceof NavigationEnd))
    )
      .pipe(delay(10), takeUntilDestroyed())
      .subscribe(() => {
        this.setupResizeObserver();
      });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------
  ngAfterViewInit() {
    setTimeout(() => {
      this.setupResizeObserver();
      this.shrinkNavigationOnOverflow();
    }, 100);
  }

  private setupResizeObserver(): void {
    this.resizeObserver.disconnect();

    if (!this.layoutService.isScreenSmall()) {
      const navigationEl = this._elementRef.nativeElement.querySelector(
        'fuse-horizontal-navigation.default-navigation'
      );
      if (navigationEl) {
        this.resizeObserver.observe(navigationEl);
      }
    }
  }

  shrinkNavigationOnOverflow(): void {
    if (this.layoutService.isScreenSmall()) {
      return;
    }

    const navigationEl = this._elementRef.nativeElement.querySelector('fuse-horizontal-navigation.default-navigation');
    const items = document.querySelectorAll(
      '.default-navigation .fuse-horizontal-navigation-item, .cdk-overlay-container .fuse-horizontal-navigation-item'
    );
    // Not the user-menu trigger: it is the company identity block (two text lines plus a chevron),
    // not a 36px icon button, so the width written below would clip it (DISTRODOT-PORTAL-MR).
    const menuItems = this._elementRef.nativeElement.querySelectorAll(
      'app-icon-menu-items button:not([data-cy="user-menu"])'
    );

    if (!navigationEl) return;

    // Baselines mirror the Figma "Main nav" spec (also set in
    // horizontal.component.scss / _buttons.scss): 14px labels, 12px item
    // padding, 20px icons, 36px icon buttons. The base applyStyles() run
    // writes these inline, so they must match the stylesheet values.
    // The logo is NOT scaled here — it keeps its fixed 40px height (Figma).
    let currentFontSize = 14;
    let currentPadding = 12;
    let currentIconMargin = 12;
    let currentIconSize = 20;
    let currentWidth = 36;

    const applyStyles = (
      fontSize: number,
      padding: number,
      iconSize: number,
      width: number,
      iconMargin: number
    ) => {
      items.forEach((item: HTMLElement) => {
        this._renderer.setStyle(item, 'font-size', `${fontSize}px`);
        this._renderer.setStyle(item, 'padding', `0 ${padding}px`);

        // Only the leading item icon — not the trailing dropdown chevron,
        // which keeps its fixed 20px size and no margin.
        const icon = item.querySelector('mat-icon.fuse-horizontal-navigation-item-icon');
        if (icon) {
          this._renderer.setStyle(icon, 'height', `${iconSize}px`);
          this._renderer.setStyle(icon, 'width', `${iconSize}px`);
          this._renderer.setStyle(icon, 'min-height', `${iconSize}px`);
          this._renderer.setStyle(icon, 'min-width', `${iconSize}px`);
          if (!icon.closest('.cdk-overlay-container')) {
            this._renderer.setStyle(icon, 'margin-right', `${iconMargin}px`);
          }
        }
      });
      menuItems.forEach((item: HTMLElement) => {
        this._renderer.setStyle(item, 'width', `${width}px`, RendererStyleFlags2.Important);

        const icon = item.querySelector('mat-icon');
        if (icon) {
          this._renderer.setStyle(icon, 'height', `${iconSize}px`);
          this._renderer.setStyle(icon, 'width', `${iconSize}px`);
          this._renderer.setStyle(icon, 'min-height', `${iconSize}px`);
          this._renderer.setStyle(icon, 'min-width', `${iconSize}px`);
        }
      });
    };

    if (navigationEl.scrollWidth === navigationEl.clientWidth) {
      applyStyles(currentFontSize, currentPadding, currentIconSize, currentWidth, currentIconMargin);
    }

    while (navigationEl.scrollWidth > navigationEl.clientWidth) {
      currentFontSize -= 0.5;
      if (currentPadding > 2.5) {
        currentPadding -= 1.5;
      }
      currentIconSize -= 0.5;
      currentWidth -= 1;
      currentIconMargin -= 1;

      applyStyles(currentFontSize, currentPadding, currentIconSize, currentWidth, currentIconMargin);
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }
}
